export type ViewerTransform = { zoom: number; x: number; y: number };

/** Keeps the same document point under the mouse at every interpolation step. */
export function anchoredZoom(
  view: ViewerTransform,
  x: number,
  y: number,
  delta: number,
): ViewerTransform {
  const zoom = Math.min(5, Math.max(0.08, view.zoom * Math.exp(-delta * 0.0015)));
  const ratio = zoom / view.zoom;
  return { zoom, x: x - (x - view.x) * ratio, y: y - (y - view.y) * ratio };
}

/** Paint only the image during wheel bursts; commit React state when settled. */
export function pointerZoomGesture(
  viewer: HTMLElement,
  read: () => ViewerTransform,
  paint: (view: ViewerTransform) => void,
  commit: (view: ViewerTransform) => void,
) {
  let shown: ViewerTransform | null = null,
    target: ViewerTransform | null = null;
  let frame = 0,
    last = 0;
  const tick = (now: number) => {
    frame = 0;
    if (!shown || !target) return;
    const amount = matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 1
      : 1 - Math.exp(-Math.min(40, now - last) / 45);
    last = now;
    shown = {
      zoom: shown.zoom + (target.zoom - shown.zoom) * amount,
      x: shown.x + (target.x - shown.x) * amount,
      y: shown.y + (target.y - shown.y) * amount,
    };
    if (
      Math.abs(shown.zoom - target.zoom) < 0.0001 &&
      Math.hypot(shown.x - target.x, shown.y - target.y) < 0.05
    ) {
      const final = target;
      shown = target = null;
      paint(final);
      commit(final);
      return;
    }
    paint(shown);
    frame = requestAnimationFrame(tick);
  };
  const finish = () => {
    cancelAnimationFrame(frame);
    frame = 0;
    const current = shown;
    shown = target = null;
    if (current) commit(current);
  };
  viewer.addEventListener('pointerdown', finish);
  return {
    wheel(event: WheelEvent) {
      event.preventDefault();
      const box = viewer.getBoundingClientRect();
      if (!shown) shown = read();
      const delta =
        event.deltaY *
        (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? viewer.clientHeight : 1);
      target = anchoredZoom(
        target ?? shown,
        event.clientX - box.left - box.width / 2,
        event.clientY - box.top - box.height / 2,
        delta,
      );
      if (!frame) {
        last = performance.now();
        frame = requestAnimationFrame(tick);
      }
    },
    dispose() {
      cancelAnimationFrame(frame);
      viewer.removeEventListener('pointerdown', finish);
    },
  };
}
